/**
 * Resume analyze agent step graph.
 *
 * Mirrors src/lib/outreach/agent/step-graph.ts. The graph being declarative is
 * what lets the dialog render the whole plan greyed-out before the run starts
 * and light rows up as they finish — a checklist rather than a spinner with a
 * rotating caption, which is what the dialog used to show.
 *
 * Strictly linear, so no parallel scheduler is needed at this level. The
 * concurrency that exists lives INSIDE SCORE_DIMENSIONS (see steps/score-dimensions.ts).
 */

import type { AgentGenerationStep, AgentStepCopy } from '@/lib/agent-kit/types';
import type { ResumeAnalyzeStepKey } from './types';

export const resumeAnalyzeStepOrder: ResumeAnalyzeStepKey[] = [
  'READ_RESUME',
  'SCORE_DIMENSIONS',
  'PRIORITIZE',
  'VALIDATE',
  'RECONCILE',
];

export const resumeAnalyzeStepDependencies: Record<
  ResumeAnalyzeStepKey,
  ResumeAnalyzeStepKey[]
> = {
  READ_RESUME: [],
  SCORE_DIMENSIONS: ['READ_RESUME'],
  PRIORITIZE: ['SCORE_DIMENSIONS'],
  VALIDATE: ['SCORE_DIMENSIONS', 'PRIORITIZE'],
  RECONCILE: ['VALIDATE'],
};

/** Present-progressive: these are read while the step is running. */
export const resumeAnalyzeStepCopy: AgentStepCopy<ResumeAnalyzeStepKey> = {
  labels: {
    READ_RESUME: 'Reading what your resume actually says',
    SCORE_DIMENSIONS: 'Scoring the six dimensions',
    PRIORITIZE: 'Ranking the fixes worth making first',
    VALIDATE: 'Checking the scores against the evidence',
    RECONCILE: 'Correcting what the check found',
  },
  descriptions: {
    READ_RESUME:
      'Takes an inventory first: roles, tenure, seniority signals, and which claims carry a number versus which are only asserted.',
    SCORE_DIMENSIONS:
      'Scores story, impact, skills, polish, authenticity and target fit against that inventory rather than against a general impression.',
    PRIORITIZE:
      'Turns the concerns into ranked fixes, ordered by how many points of the overall score each one is actually worth.',
    VALIDATE:
      'A different model checks whether each score is justified by the evidence given for it, and whether the concerns are specific enough to act on.',
    RECONCILE:
      'Adjusts unjustified scores and sharpens vague concerns. Skipped when the check passes.',
  },
};

/**
 * All steps PENDING — the plan, published before any work begins.
 *
 * Lives here rather than in run.ts on purpose: the dialog is a client component,
 * and run.ts transitively imports the model steps (and therefore @ai-sdk/openai
 * and the API key). Importing it from run.ts would drag the server-side agent
 * into the browser bundle.
 */
export function initialResumeAnalyzeSteps(): AgentGenerationStep<ResumeAnalyzeStepKey>[] {
  return resumeAnalyzeStepOrder.map((step_key) => ({ step_key, status: 'PENDING' }));
}

/** Bump when the prompts or the graph change, so stale traces are identifiable. */
export const RESUME_ANALYZE_AGENT_VERSION = 'v1';
